import React, { SyntheticEvent, useMemo, useRef, useState } from 'react';
import UserAvatar from '@components/UserAvatar';
import { Select } from 'antd';
import type { CustomTagProps } from 'rc-select/lib/BaseSelect';
import { MemberDto, PageDto } from '@api/Api';
import { useDebounced } from '@helpers/useDebounced';
import { nonNull } from '@helpers/non-null';
import { emailValidatorRegExp } from '@helpers/validators';
import {
  QueryFunctionContext,
  QueryKey,
  useInfiniteQuery
} from '@tanstack/react-query';

type TData<TMember extends MemberDto> = PageDto & {
  edges: {
    node: TMember;
    cursor: string;
  }[];
};

interface InviteMembersSelectProps<TMember extends MemberDto> {
  mode?: 'id-or-email' | 'email';
  value?: string[];
  onChange?: (value: string[]) => void;
  queryKey: QueryKey;
  queryFn: (
    context: QueryFunctionContext<QueryKey, string | undefined> & {
      searchQuery: string;
    }
  ) => Promise<TData<TMember>>;
  queryEnabled?: boolean;
}

export default function InviteMembersSelect<
  TMember extends MemberDto = MemberDto
>({
  mode,
  value,
  onChange,
  queryKey,
  queryFn,
  queryEnabled
}: InviteMembersSelectProps<TMember>) {
  const membersCache = useRef<Map<string, MemberDto>>(new Map());
  const [searchQuery, setSearchQuery] = useState<string>('');
  const debouncedSearchQuery = useDebounced(searchQuery, 250);
  const [dropdownVisible, setDropdownVisible] = useState<boolean>(false);
  const membersQuery = useInfiniteQuery({
    queryKey: [...queryKey, debouncedSearchQuery] as QueryKey,
    keepPreviousData: true,
    enabled: queryEnabled,
    queryFn: (ctx) =>
      queryFn({
        ...ctx,
        pageParam: ctx.pageParam || undefined,
        searchQuery: debouncedSearchQuery
      }),
    getNextPageParam: (lastPage) =>
      lastPage.hasNext ? lastPage.endCursor : undefined
  });
  const membersList = useMemo(
    () =>
      membersQuery.data?.pages
        .flatMap((x) => x.edges.map((x) => x.node))
        .filter((x) => !x.user?.me) ?? [],
    [membersQuery.data]
  );
  const handleChange = (values: string[]) => {
    const prev = value;
    const newValues = values
      .flatMap((value) => value.split(' '))
      .map((value) => value.trim().toLowerCase())
      .filter((value) => !!value)
      .map((value) => {
        if (!value) return undefined;
        if (emailValidatorRegExp.test(value)) return value;
        if (prev?.includes(value)) return value;
        const memberByIdOrEmail = membersList.find(
          (member) =>
            member.id === value ||
            member.user?.email === value ||
            member.invitationEmail === value
        );
        if (memberByIdOrEmail) {
          const email =
            memberByIdOrEmail.user?.email || memberByIdOrEmail.invitationEmail;
          const idOrEmail =
            mode === 'email' ? nonNull(email) : memberByIdOrEmail.id;
          membersCache.current.set(idOrEmail, memberByIdOrEmail);
          return idOrEmail;
        }
        const membersBySearch = membersList.filter((member) => {
          const email =
            member.user?.email.toLowerCase() ??
            member.invitationEmail?.toLowerCase();
          const name = member.user?.name.toLowerCase();
          return email?.includes(value) || name?.includes(value);
        });
        const memberBySearch =
          membersBySearch.length > 0 ? membersBySearch[0] : undefined;
        if (memberBySearch) {
          const email =
            memberBySearch.user?.email || memberBySearch.invitationEmail;
          const idOrEmail =
            mode === 'email' ? nonNull(email) : memberBySearch.id;
          membersCache.current.set(idOrEmail, memberBySearch);
          return idOrEmail;
        }
        return undefined;
      })
      .filter((x) => !!x)
      .map((x) => nonNull(x));
    if (onChange) onChange(Array.from(new Set(newValues)));
    setDropdownVisible(false);
    setSearchQuery('');
  };
  const loadMoreData = (e: SyntheticEvent) => {
    if (!membersQuery.hasNextPage) return;
    const target = e.target as HTMLDivElement;
    if (target.scrollTop + target.offsetHeight === target.scrollHeight) {
      membersQuery.fetchNextPage();
    }
  };
  const tagRender = useMemo(() => {
    return function Tag({ value, onClose }: CustomTagProps) {
      const member = membersCache.current.get(value);
      return (
        <span className="selected_item">
          <span className="selected_item__content">
            {!member && (
              <span className="new main-body-text main-body-text--tiny main-body-text--white main-body-text--bold">
                new
              </span>
            )}
            <span className="name main-body-text">
              {member?.user?.name || member?.invitationEmail || value}
            </span>
            <span className="close" onClick={onClose} />
          </span>
        </span>
      );
    };
  }, []);
  const open = dropdownVisible && membersList.length > 0;
  const loading = membersQuery.isLoading || membersQuery.isPreviousData;
  return (
    <Select
      mode="tags"
      size="large"
      listHeight={280}
      placeholder="Invite by email or from list"
      tokenSeparators={[',', '\r', '\n']}
      showArrow={open || loading}
      filterOption={false}
      searchValue={searchQuery}
      onSearch={setSearchQuery}
      onChange={handleChange}
      style={{ width: '100%', marginBottom: 16 }}
      popupClassName="invitation_user_list"
      onPopupScroll={loadMoreData}
      tagRender={tagRender}
      open={open}
      onDropdownVisibleChange={(v) => setDropdownVisible(v)}
      value={value}
      loading={loading}
    >
      {membersList.map((member) => (
        <Select.Option key={member.id}>
          <div className="list_item">
            <div className="list_item__avatar">
              <UserAvatar
                isActive={!member.invitationEmail}
                size="large"
                src={member.user?.picture.url || ''}
                userEmail={member.user?.email || ''}
                userName={member.user?.name || ''}
              />
            </div>
            <div className="list_item__user_info">
              <div className="list_item__user_info__name">
                {member.user?.name}
              </div>
              <div className="list_item__user_info__email">
                {member.user?.email || member.invitationEmail}
              </div>
            </div>
          </div>
        </Select.Option>
      ))}
    </Select>
  );
}

InviteMembersSelect.defaultProps = {
  mode: 'id-or-email'
};
